import React, { useCallback, useEffect, useRef, useState } from "react"
import { Calendar } from "react-date-range"
import { addYears } from "date-fns"
import { enUS as defaultLocale } from "date-fns/locale"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"

import { formatDate } from "./formatDate"
import { IoCalendarNumberOutline } from "react-icons/io5"

/** True if calendar month (0–11) overlaps [minDate, maxDate]. */
function monthSelectable (year, monthIdx, minDate, maxDate) {
	const monthFirst = new Date(year, monthIdx, 1)
	const monthLast = new Date(year, monthIdx + 1, 0, 23, 59, 59, 999)
	if (minDate) {
		const minDay = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
		if (monthLast < minDay) return false
	}
	if (maxDate) {
		const maxDay = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 23, 59, 59, 999)
		if (monthFirst > maxDay) return false
	}
	return true
}

function yearSelectable (year, minDate, maxDate) {
	const first = new Date(year, 0, 1)
	const last = new Date(year, 11, 31, 23, 59, 59, 999)
	if (minDate) {
		const minDay = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
		if (last < minDay) return false
	}
	if (maxDate) {
		const maxDay = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 23, 59, 59, 999)
		if (first > maxDay) return false
	}
	return true
}

/**
 * Mirrors react-date-range's default navigator but disables month/year `<option>`
 * values outside min/max (the upstream library omits disabled, breaking styling).
 */
function claritickNavigatorRenderer (focusedDate, changeShownDate, props) {
	const {
		showMonthArrow = true,
		minDate,
		maxDate,
		showMonthAndYearPickers = true,
		ariaLabels = {},
		locale = defaultLocale,
	} = props

	const upperYearLimit = maxDate?.getFullYear() ?? addYears(new Date(), 20).getFullYear()
	const lowerYearLimit = minDate?.getFullYear() ?? addYears(new Date(), -100).getFullYear()
	const monthNames = [...Array(12).keys()].map((i) => locale.localize.month(i))

	return (
		<div className="rdrMonthAndYearWrapper" onMouseUp={(e) => e.stopPropagation()}>
			{showMonthArrow ? (
				<button
					className="rdrNextPrevButton rdrPprevButton"
					type="button"
					onClick={() => changeShownDate(-1, "monthOffset")}
					aria-label={ariaLabels.prevButton}
				>
					<i />
				</button>
			) : null}
			{showMonthAndYearPickers ? (
				<span className="rdrMonthAndYearPickers">
					<span className="rdrMonthPicker">
						<select
							value={focusedDate.getMonth()}
							onChange={(e) => changeShownDate(Number(e.target.value), "setMonth")}
							aria-label={ariaLabels.monthPicker}
						>
							{monthNames.map((monthName, i) => {
								const selectable = monthSelectable(
									focusedDate.getFullYear(),
									i,
									minDate,
									maxDate,
								)
								return (
									<option key={i} value={i} disabled={!selectable}>
										{monthName}
									</option>
								)
							})}
						</select>
					</span>
					<span className="rdrMonthAndYearDivider" />
					<span className="rdrYearPicker">
						<select
							value={focusedDate.getFullYear()}
							onChange={(e) => changeShownDate(Number(e.target.value), "setYear")}
							aria-label={ariaLabels.yearPicker}
						>
							{Array.from({ length: upperYearLimit - lowerYearLimit + 1 }, (_, i) =>
								upperYearLimit - i).map((year) => {
								const selectable = yearSelectable(year, minDate, maxDate)
								return (
									<option key={year} value={year} disabled={!selectable}>
										{year}
									</option>
								)
							})}
						</select>
					</span>
				</span>
			) : (
				<span className="rdrMonthAndYearPickers">
					{monthNames[focusedDate.getMonth()]}{" "}
					{focusedDate.getFullYear()}
				</span>
			)}
			{showMonthArrow ? (
				<button
					className="rdrNextPrevButton rdrNextButton"
					type="button"
					onClick={() => changeShownDate(1, "monthOffset")}
					aria-label={ariaLabels.nextButton}
				>
					<i />
				</button>
			) : null}
		</div>
	)
}


const style= {
    window:  {
        boxShadow: '0 6px 15px rgba(37,49,60,.16)',
        fontFamily: "Poppins-Regular",
        position: 'absolute',
        right: 0,
        top: "39px",
        zIndex: 9999
    },
    wrapper: {
        position: 'relative'
    },
    display: {
        display: "flex",
        alignItems: "center",
        color: "#1675e0",
        justifyContent: "flex-end",
        cursor: "pointer"
    },
    icon: {
        fontSize: "16px",
        marginRight: "5px",
        color: "grey"
    },
    space: {
        marginRight: "5px"
    },
    input: {
        color: "#575757",
        border:" 1px solid #e5e5ea",
        width: "100%",
        height: "38px",
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        padding: "10px",
        cursor: "pointer",
        justifyContent: "space-between"
    },
    inputInvalid: {
        borderColor: "#dc3545",
    },
    inputDisabled: {
        opacity: 0.65,
        cursor: "not-allowed",
        pointerEvents: "none",
        backgroundColor: "#e9ecef",
    },
}

const DatePicker = ({ handleDateChange, selected, minDate, maxDate, disabled = false, invalid = false }) => {
    const wrapperRef = useRef()
    const [selectedDate, setSelectedDate] = useState(null)
    const [showCalendar, setShowCalendar] = useState(false)

	const navigatorRenderer = useCallback(claritickNavigatorRenderer, [])

    useEffect(() => {
        if (selected) {
            setSelectedDate(selected);
        } else {
            setSelectedDate(null);
        }
    }, [selected]);

    useEffect(() => {
        if (disabled) {
            setShowCalendar(false);
        }
    }, [disabled]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const toggleCalendar = () => {
        if (disabled) {
            return;
        }
        setShowCalendar((open) => !open);
    };

    return(
        <div style={style.wrapper} ref={wrapperRef}>
            <div
                style={style.display}
                onClick={toggleCalendar}
                aria-disabled={disabled || undefined}
            >
                <div style={{ ...style.input, ...(invalid ? style.inputInvalid : {}), ...(disabled ? style.inputDisabled : {}) }}>
                    {selectedDate ? formatDate(selectedDate) : "Select Date"}
                    <IoCalendarNumberOutline/>
                </div>   
            </div>
            {showCalendar && !disabled &&
                <div className="date-range-picker" style={style.window}>
                    <Calendar
                        date={selectedDate}
                        minDate={minDate}
                        maxDate={maxDate}
                        navigatorRenderer={navigatorRenderer}
                        onChange={(item) => {
                            setSelectedDate(item);
                            setShowCalendar(false);
                            handleDateChange(item);
                        }}
                    />
                </div>
            }
    </div>
    )
}
export default DatePicker